// The Principals panel (BUILD-BRIEF-R2 §4.1, §13) — the same component on Agents and on Admin.
//
// A principal is a caller with scopes: hermes, claude-code, scout, signet. Creating one answers
// with a bearer that is shown ONCE, here, and never again — the server keeps only its sha256.
// Revoking takes effect on the next call; rotating mints a new token and kills the old one. Every
// figure on the row is counted from the ledger (calls in 24 h) or read from the row itself, so
// none carries a claim mark: this table is arithmetic, not assertion.
import { useCallback, useMemo, useState } from 'react';
import { createPrincipal, listPrincipals, revokePrincipal, rotatePrincipal } from '../api/principals.js';
import { Fig, Lnk, Ref, Sheet, Tag, Tbl, Zone, ZoneHead } from './PageKit.js';
import ConfirmModal from './ConfirmModal.js';
import { useToast } from './Toast.js';
import { usePoll } from './usePoll.js';
import { useLocale } from '../i18n/index.js';
import { tCtx } from '../../shared/i18n.js';
import { SCOPES, type Principal, type PrincipalKind, type Scope } from '../../shared/types.js';
import './PrincipalsPanel.de.js';
import './PrincipalsPanel.fr.js';

export interface PrincipalsPanelProps {
  /** Admin renders the same panel in a narrower column than the Agents zone does. */
  compact?: boolean;
  /** A counter the host page bumps to open the create form (the masthead's "New principal"). */
  createRequested?: number;
}

/** §4.1's defaults when the operator creates one of the four named principals. */
const DEFAULT_SCOPES: Record<string, Scope[]> = {
  hermes: ['read', 'propose', 'scout:request'],
  'claude-code': ['read', 'propose'],
  scout: ['snapshot:write'],
  signet: ['read'],
};
const DEFAULT_KIND: Record<string, PrincipalKind> = { hermes: 'external', 'claude-code': 'external', scout: 'worker', signet: 'external' };
const KINDS: PrincipalKind[] = ['external', 'internal', 'worker', 'system'];

interface Shown { name: string; token: string; reason: 'created' | 'rotated' }

export default function PrincipalsPanel({ compact, createRequested = 0 }: PrincipalsPanelProps) {
  const { t, fmtDateTime, fmtDate } = useLocale();
  const toast = useToast();
  const fetchPrincipals = useCallback(() => listPrincipals(), []);
  const poll = usePoll(fetchPrincipals, 30_000);
  const items = poll.data?.items ?? null;

  const [formOpen, setFormOpen] = useState(false);
  const [seenRequest, setSeenRequest] = useState(0);
  if (createRequested !== seenRequest) {
    // A one-shot arrival intent from the masthead: open the form once per bump.
    setSeenRequest(createRequested);
    if (createRequested > 0) setFormOpen(true);
  }
  const [shown, setShown] = useState<Shown | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'revoke' | 'rotate'; principal: Principal } | null>(null);
  const [busy, setBusy] = useState(false);

  const live = useMemo(() => (items ?? []).filter((p) => !p.revoked_at), [items]);

  const act = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'revoke') {
        await revokePrincipal(confirm.principal.id);
        toast('ok', t('{name} revoked. Its next call answers 401.', { name: confirm.principal.name }));
      } else {
        const res = await rotatePrincipal(confirm.principal.id);
        setShown({ name: res.principal.name, token: res.token, reason: 'rotated' });
      }
      await poll.refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const statusTag = (p: Principal) => {
    if (p.revoked_at) return <Tag tone="broken" title={fmtDateTime(p.revoked_at)}>{t('revoked')}</Tag>;
    if (p.expires_at && new Date(p.expires_at).getTime() < Date.now()) return <Tag tone="broken">{t('expired')}</Tag>;
    if (p.has_token === false) return <Tag tone="ghost">{t('no token')}</Tag>;
    if (p.expires_at) return <Tag tone="attend">{t('expires {date}', { date: fmtDate(p.expires_at) })}</Tag>;
    return <Tag tone="ok">{tCtx(t, 'live', 'principal')}</Tag>;
  };

  return (
    <Zone id="principals">
      <ZoneHead
        label={t('Principals')}
        count={items ? live.length : undefined}
        note={items ? t('who may call what · a bearer shown once, stored as a hash') : poll.notice ?? t('Loading…')}
        route={
          <button type="button" className="zone-route" onClick={() => setFormOpen((v) => !v)}>
            {formOpen ? t('Close form') : t('New principal →')}
          </button>
        }
      />

      {shown && (
        <Sheet edge pad style={{ marginBottom: 12 }}>
          <div className="u-label" style={{ marginBottom: 6 }}>
            {shown.reason === 'created' ? t('Token for {name} — shown once', { name: shown.name }) : t('New token for {name} — shown once', { name: shown.name })}
          </div>
          <p className="rat" style={{ marginBottom: 8 }}>
            {t('Copy it now into the caller\'s environment. The server keeps only its hash; there is no way to see it again — losing it means rotating.')}
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Ref className="voice-record" title={t('the bearer token')}>{shown.token}</Ref>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                navigator.clipboard?.writeText(shown.token).then(
                  () => toast('ok', t('Token copied.')),
                  () => toast('warn', t('Could not copy; select it by hand.')),
                );
              }}
            >
              {t('Copy')}
            </button>
            <Lnk onClick={() => setShown(null)}>{t('I have stored it')}</Lnk>
          </div>
        </Sheet>
      )}

      {formOpen && (
        <CreateForm
          existing={(items ?? []).map((p) => p.name)}
          onCreated={(p, token) => {
            setShown({ name: p.name, token, reason: 'created' });
            setFormOpen(false);
            void poll.refresh();
          }}
          onCancel={() => setFormOpen(false)}
        />
      )}

      <Sheet clip>
        {items === null ? (
          <p className="zone-empty">{poll.error ? t('Could not load the principals: {error}', { error: poll.error }) : t('Loading…')}</p>
        ) : items.length === 0 ? (
          <p className="zone-empty">
            {t('No principal exists yet. A principal is a caller with scopes — hermes, claude-code, scout, signet — and a bearer shown once at creation and never again. Every call it makes lands in the ledger, refusals included; revoking it is one click and takes effect on the next call.')}
          </p>
        ) : (
          <Tbl>
            <thead>
              <tr>
                <th>{t('Principal')}</th>
                {!compact && <th>{t('Kind')}</th>}
                <th>{t('Scopes')}</th>
                <th>{t('Token')}</th>
                {!compact && <th className="num">{t('Rate')}</th>}
                <th className="num">{t('Last seen')}</th>
                <th className="num">{t('Calls · 24h')}</th>
                <th>{t('Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} style={p.revoked_at ? { opacity: 0.6 } : undefined}>
                  <td><span className="w">{p.name}</span></td>
                  {!compact && <td>{t(p.kind)}</td>}
                  <td>
                    {p.scopes.length === 0 && <span className="d">{t('none')}</span>}
                    {p.scopes.map((s) => <span key={s} style={{ marginRight: 4, display: 'inline-block' }}><Tag>{s}</Tag></span>)}
                  </td>
                  <td>{p.token_prefix ? <Ref>{p.token_prefix}…</Ref> : <span className="d">{t('none')}</span>}</td>
                  {!compact && (
                    <td className="num">
                      <Fig unit={t('/min')}>{p.rate_per_minute}</Fig> <span className="d">·</span> <Fig unit={t('conc.')}>{p.max_concurrency}</Fig>
                    </td>
                  )}
                  <td className="num">
                    {p.last_seen_at ? <Fig>{fmtDateTime(p.last_seen_at)}</Fig> : <span className="fv--unknown voice-figure">{t('never')}</span>}
                  </td>
                  <td className="num">
                    {p.calls_24h === undefined
                      ? <span className="fv--unknown voice-figure">{t('not collected')}</span>
                      : <Fig className={p.calls_24h === 0 ? 'fv--nil' : undefined}>{p.calls_24h}</Fig>}
                  </td>
                  <td>{statusTag(p)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {!p.revoked_at && (
                      <>
                        <Lnk onClick={() => setConfirm({ kind: 'rotate', principal: p })} title={t('Mint a new token; the old one stops working at once.')}>{t('Rotate…')}</Lnk>
                        {' '}
                        <Lnk onClick={() => setConfirm({ kind: 'revoke', principal: p })} title={t('The next call with this token answers 401.')}>
                          <span style={{ color: 'var(--broken)' }}>{t('Revoke…')}</span>
                        </Lnk>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tbl>
        )}
      </Sheet>

      <ConfirmModal
        open={confirm !== null}
        title={confirm?.kind === 'revoke'
          ? (confirm?.principal.kind === 'worker'
            ? t('Revoke {name}? This is the token your scout runs with.', { name: confirm?.principal.name ?? '' })
            : t('Revoke {name}?', { name: confirm?.principal.name ?? '' }))
          : t('Rotate the token of {name}?', { name: confirm?.principal.name ?? '' })}
        confirmLabel={busy ? t('Saving…') : confirm?.kind === 'revoke' ? t('Revoke') : t('Rotate')}
        danger={confirm?.kind === 'revoke'}
        onConfirm={() => { if (!busy) void act(); }}
        onCancel={() => setConfirm(null)}
      >
        <p className="rat">
          {confirm?.kind === 'revoke'
            ? t('Its next call answers 401 and is not ledgered. The ledger rows it already wrote stay under its name. This cannot be undone; create a new principal instead.')
            : t('A new bearer is minted and shown once; the current one stops working at the same instant. Update the caller\'s environment right after.')}
        </p>
      </ConfirmModal>
    </Zone>
  );
}

// ── the create form ─────────────────────────────────────────────────────────

function CreateForm({ existing, onCreated, onCancel }: {
  existing: string[];
  onCreated: (p: Principal, token: string) => void;
  onCancel: () => void;
}) {
  const { t } = useLocale();
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<PrincipalKind>('external');
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [touchedScopes, setTouchedScopes] = useState(false);
  const [rate, setRate] = useState('60');
  const [conc, setConc] = useState('2');
  const [expires, setExpires] = useState('');
  const [saving, setSaving] = useState(false);

  const onName = (v: string) => {
    const n = v.trim().toLowerCase();
    setName(v);
    if (DEFAULT_SCOPES[n]) {
      if (!touchedScopes) setScopes(DEFAULT_SCOPES[n]);
      setKind(DEFAULT_KIND[n]);
    }
  };
  const toggle = (s: Scope) => {
    setTouchedScopes(true);
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };
  const trimmed = name.trim().toLowerCase();
  const nameTaken = existing.includes(trimmed);
  const nameOk = /^[a-z][a-z0-9._:-]{1,63}$/.test(trimmed) && !nameTaken;
  const canSave = nameOk && scopes.length > 0 && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const res = await createPrincipal({
        name: trimmed,
        kind,
        scopes,
        rate_per_minute: Number(rate) || 60,
        max_concurrency: Number(conc) || 2,
        expires_at: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
      });
      onCreated(res.principal, res.token);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet pad style={{ marginBottom: 12 }}>
      <div className="u-label" style={{ marginBottom: 8 }}>{t('New principal')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="pp-name">{t('Name')}</label>
          <input
            id="pp-name"
            className="form-input"
            list="pp-names"
            value={name}
            onChange={(e) => onName(e.target.value)}
            placeholder="hermes · claude-code · scout · signet"
            autoComplete="off"
          />
          <datalist id="pp-names">
            {Object.keys(DEFAULT_SCOPES).filter((n) => !existing.includes(n)).map((n) => <option key={n} value={n} />)}
          </datalist>
          {nameTaken && <div className="d" style={{ color: 'var(--broken)' }}>{t('A principal with this name exists; rotate or revoke it instead.')}</div>}
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="pp-kind">{t('Kind')}</label>
          <select id="pp-kind" className="form-select" value={kind} onChange={(e) => setKind(e.target.value as PrincipalKind)}>
            {KINDS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="pp-rate">{t('Rate per minute')}</label>
          <input id="pp-rate" className="form-input" type="number" min={1} max={6000} value={rate} onChange={(e) => setRate(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="pp-conc">{t('Max concurrency')}</label>
          <input id="pp-conc" className="form-input" type="number" min={1} max={64} value={conc} onChange={(e) => setConc(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="pp-exp">{t('Expires on')}</label>
          <input id="pp-exp" className="form-input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
      </div>
      <div className="form-group" style={{ marginTop: 8 }}>
        <span className="form-label">{t('Scopes')}</span>
        <div className="chips">
          {SCOPES.map((s) => (
            <label key={s} className="chip" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} style={{ marginRight: 6 }} />
              {s}
            </label>
          ))}
          <span className="chip" title={t('Sending is an operator click on the Inbox. The registry refuses to boot with a send scope.')} style={{ opacity: 0.6 }}>
            <Tag tone="ghost">send</Tag> <span className="d">{t('never')}</span>
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
        <button type="button" className="btn btn-accent" disabled={!canSave} onClick={() => void submit()}>
          {saving ? t('Saving…') : t('Create and show the token')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('Cancel')}</button>
        <span className="d">{t('The token appears once, right here, after creation.')}</span>
      </div>
    </Sheet>
  );
}
